/**
 * UltraAdminModularPackagesPage
 * ─────────────────────────────
 * Ultra Admin, her m² seçeneği için (25/50/100/150 veya özel yeni) bir modüler
 * stand paketi tanımlar. Paket = ad, açıklama, toplu fiyat, currency + items[].
 * Her item: organizer_products'tan seçilir (autocomplete) + qty + extra_allowed.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../lib/apiClient';
import {
  Package, Plus, Minus, X, Edit2, Trash2, ChevronDown, Search,
  Layers, RotateCcw, Check, AlertCircle, CopyPlus
} from 'lucide-react';
import AdminSidebar from '../components/AdminSidebar';
import UltraAdminTopbar from '../components/UltraAdminTopbar';
import PageIntro from '../components/PageIntro';
import { getPageIntro } from '../lib/pageIntros';
import ConfirmDialog from '../components/ConfirmDialog';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';

export default function UltraAdminModularPackagesPage() {
  const { language } = useLanguage();
  const tr = language === 'tr';
  const navigate = useNavigate();

  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | pkg obj
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [bulkAddOpen, setBulkAddOpen] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('cop31_ultra_token') || localStorage.getItem('cop31_token');
    if (!token) navigate('/giris?tab=ultra');
  }, [navigate]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const r = await apiClient.get('/ultra-admin/modular-packages', { params: { include_inactive: true } });
      setPackages(r.data || []);
    } catch (e) {
      toast.error(tr ? 'Paketler yüklenemedi.' : 'Failed to load packages.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, []);

  const handleDelete = (pkg) => setConfirmDelete(pkg);

  const performDelete = async () => {
    if (!confirmDelete) return;
    try {
      await apiClient.delete(`/ultra-admin/modular-packages/${confirmDelete.id}`);
      toast.success(tr ? 'Paket kalıcı olarak silindi.' : 'Package permanently deleted.');
      setConfirmDelete(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <AdminSidebar />
      <div className="flex-1 overflow-auto">
        <UltraAdminTopbar
          title={tr ? 'Modüler Stand Paketleri' : 'Modular Stand Packages'}
          subtitle={tr ? 'Her m² seçeneği için paket tanımı' : 'Define a package per m² option'}
          icon={Layers}
          onRefresh={fetchAll}
          rightExtra={
            <div className="flex items-center gap-2">
              <button
                onClick={() => setBulkAddOpen(true)}
                disabled={packages.length === 0}
                data-testid="bulk-add-item-btn"
                title={tr ? 'Bir ürünü birden çok pakete aynı anda ekle' : 'Add a product to multiple packages at once'}
                className="flex items-center gap-2 bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-800 font-semibold px-3 py-2 rounded-xl text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <CopyPlus className="h-4 w-4" />
                {tr ? 'Toplu Ürün Ekle' : 'Bulk Add Product'}
              </button>
              <button
                onClick={() => setEditing('new')}
                data-testid="create-package-btn"
                className="flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white font-semibold px-4 py-2 rounded-xl text-sm shadow-sm"
              >
                <Plus className="h-4 w-4" /> {tr ? 'Yeni Paket' : 'New Package'}
              </button>
            </div>
          }
        />

        <div className="px-8 pt-5">
          <PageIntro {...getPageIntro('modular-packages', language)} />
        </div>

        {/* Content */}
        <div className="p-8">
          {loading ? (
            <div className="text-center py-16">
              <div className="animate-spin h-10 w-10 border-4 border-indigo-500 border-t-transparent rounded-full mx-auto mb-3" />
            </div>
          ) : packages.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
              <Layers className="h-14 w-14 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500 mb-3">{tr ? 'Henüz paket tanımlanmamış.' : 'No packages defined yet.'}</p>
              <button
                onClick={() => setEditing('new')}
                className="inline-flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white font-semibold px-4 py-2 rounded-xl text-sm"
              >
                <Plus className="h-4 w-4" /> {tr ? 'İlk Paketi Ekle' : 'Add First Package'}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {packages.map(p => (
                <PackageCard
                  key={p.id}
                  pkg={p}
                  tr={tr}
                  onEdit={() => setEditing(p)}
                  onDelete={() => handleDelete(p)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {editing && (
        <PackageEditorModal
          tr={tr}
          pkg={editing === 'new' ? null : editing}
          allPackages={packages}
          onClose={() => { setEditing(null); fetchAll(); }}
        />
      )}

      {bulkAddOpen && (
        <BulkAddItemModal
          tr={tr}
          packages={packages}
          onClose={(didChange) => {
            setBulkAddOpen(false);
            if (didChange) fetchAll();
          }}
        />
      )}

      {/* Delete confirmation */}
      <ConfirmDialog
        open={!!confirmDelete}
        title={tr ? 'Paketi Kalıcı Olarak Sil' : 'Permanently Delete Package'}
        message={tr
          ? `${confirmDelete?.area_m2 || ''} m² — "${(confirmDelete?.name_tr || confirmDelete?.name) || ''}" paketini kalıcı olarak silmek istediğinize emin misiniz?\n\nBu işlem geri alınamaz.`
          : `Permanently delete ${confirmDelete?.area_m2 || ''} m² — "${(confirmDelete?.name_en || confirmDelete?.name) || ''}"?\n\nThis action cannot be undone.`
        }
        confirmLabel={tr ? 'Evet, Kalıcı Sil' : 'Yes, Delete Permanently'}
        cancelLabel={tr ? 'Vazgeç' : 'Cancel'}
        variant="danger"
        onConfirm={performDelete}
        onCancel={() => setConfirmDelete(null)}
        testId="confirm-delete-package"
      />
    </div>
  );
}

/* ─── Package card ─────────────────────────────────────────────────────────── */
function PackageCard({ pkg, tr, onEdit, onDelete }) {
  const items = pkg.items || [];
  const pkgName = (tr ? (pkg.name_tr || pkg.name) : (pkg.name_en || pkg.name)) || pkg.name;
  const pkgDesc = (tr ? (pkg.description_tr || pkg.description) : (pkg.description_en || pkg.description)) || pkg.description;
  return (
    <div className={`bg-white rounded-2xl border overflow-hidden ${pkg.is_active ? 'border-gray-200' : 'border-gray-200 opacity-60'}`} data-testid={`package-card-${pkg.id}`}>
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-500 to-blue-600 px-5 py-4 text-white">
        <div className="flex items-center justify-between mb-1">
          <span className="bg-white/20 text-white text-[10px] font-bold px-2 py-0.5 rounded-full tracking-widest">
            {pkg.area_m2} m²
          </span>
          {!pkg.is_active && (
            <span className="bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
              {tr ? 'PASİF' : 'INACTIVE'}
            </span>
          )}
        </div>
        <h3 className="font-bold text-lg leading-tight">{pkgName}</h3>
        {pkgDesc && <p className="text-white/80 text-xs mt-1 line-clamp-2">{pkgDesc}</p>}
      </div>
      {/* Body */}
      <div className="p-5 space-y-3">
        <div>
          <p className="text-xs font-semibold text-gray-700 mb-1.5">
            {tr ? `İçindekiler (${items.length})` : `Items (${items.length})`}
          </p>
          {items.length === 0 ? (
            <p className="text-xs text-gray-400 italic">{tr ? 'Kalem eklenmedi' : 'No items'}</p>
          ) : (
            <ul className="space-y-1 max-h-40 overflow-y-auto pr-1">
              {items.map((it, i) => {
                const itemName = (tr ? (it.product?.name_tr || it.product?.name) : (it.product?.name_en || it.product?.name)) || '—';
                return (
                  <li key={i} className="flex items-center gap-2 text-xs bg-gray-50 rounded-lg px-2 py-1.5">
                    {it.product?.thumb_url
                      ? <img src={it.product.thumb_url} alt={itemName} className="h-7 w-7 rounded object-cover flex-shrink-0" />
                      : <div className="h-7 w-7 rounded bg-gray-200 flex-shrink-0" />}
                    <span className="flex-1 font-medium text-gray-700 truncate">{itemName}</span>
                    <span className="font-bold text-gray-900">×{it.qty}</span>
                    {it.extra_allowed && (
                      <span title={tr ? 'Ekstra eklenebilir' : 'Extras allowed'} className="text-green-600 font-bold">+</span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="flex items-center gap-1.5 pt-1">
          <button
            onClick={onEdit}
            data-testid={`edit-package-${pkg.id}`}
            className="flex-1 flex items-center justify-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold text-xs py-2 rounded-lg"
          >
            <Edit2 className="h-3.5 w-3.5" /> {tr ? 'Düzenle' : 'Edit'}
          </button>
          <button
            onClick={onDelete}
            className="p-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-600"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Editor modal ─────────────────────────────────────────────────────────── */
function PackageEditorModal({ tr, pkg, allPackages = [], onClose }) {
  const isEdit = !!pkg;
  const [form, setForm] = useState({
    area_m2:          pkg?.area_m2 ?? 50,
    name_tr:          pkg?.name_tr || pkg?.name || '',
    name_en:          pkg?.name_en || pkg?.name || '',
    description_tr:   pkg?.description_tr || pkg?.description || '',
    description_en:   pkg?.description_en || pkg?.description || '',
    total_price:      pkg?.total_price ?? 0,
    currency:         pkg?.currency || 'EUR',
    is_active:        pkg?.is_active ?? true,
    modular_stand_id: pkg?.modular_stand_id || '',
  });
  const [items, setItems] = useState(
    (pkg?.items || []).map(it => ({
      product_id:    it.product_id,
      qty:           it.qty,
      extra_allowed: it.extra_allowed,
      product:       it.product, // for display
    }))
  );
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [productCatalog, setProductCatalog] = useState([]);
  const [catalogSearch, setCatalogSearch] = useState('');
  const [standList, setStandList] = useState([]); // Modular Stands (platform products)

  useEffect(() => {
    const load = async () => {
      try {
        const r = await apiClient.get('/ultra-admin/organizer-products', { params: { is_active: true } });
        setProductCatalog(r.data || []);
      } catch {
        toast.error(tr ? 'Ürün kataloğu yüklenemedi.' : 'Failed to load product catalog.');
      }
    };
    load();
  }, [tr]);

  // Load Modular Stands (Platform Products) for the dropdown
  useEffect(() => {
    const loadStands = async () => {
      try {
        const r = await apiClient.get('/supplier/products', { params: { is_active: true } });
        setStandList(r.data || []);
      } catch {
        // non-blocking
      }
    };
    loadStands();
  }, []);

  const set = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    setErrors(e => { const n = { ...e }; delete n[k]; return n; });
  };

  // When a Modular Stand is selected, auto-fill name fields + parse area from title
  const handleStandSelect = (standId) => {
    if (!standId) {
      set('modular_stand_id', '');
      return;
    }
    const stand = standList.find(s => s.id === standId);
    if (!stand) return;
    // Extract area from title (e.g., "Tier 1 - 25 m² Modüler Stand" → 25)
    const standTitle = stand.title || stand.title_tr || '';
    const m = standTitle.match(/(\d+)\s*m/i);
    const parsedArea = m ? parseInt(m[1], 10) : null;
    setForm(f => ({
      ...f,
      modular_stand_id: standId,
      name_tr:          stand.title_tr || stand.title || f.name_tr,
      name_en:          stand.title_en || stand.title || f.name_en,
      area_m2:          parsedArea || f.area_m2,
    }));
    setErrors(e => ({ ...e, name: undefined, area_m2: undefined, modular_stand_id: undefined }));
  };

  const filteredCatalog = useMemo(() => {
    const q = catalogSearch.trim().toLowerCase();
    const selectedIds = new Set(items.map(it => it.product_id));
    return productCatalog.filter(p => {
      if (selectedIds.has(p.id)) return false;
      if (!q) return true;
      const n  = (p.name || '').toLowerCase();
      const nt = (p.name_tr || '').toLowerCase();
      const ne = (p.name_en || '').toLowerCase();
      const s  = (p.sku || '').toLowerCase();
      return n.includes(q) || nt.includes(q) || ne.includes(q) || s.includes(q);
    });
  }, [productCatalog, catalogSearch, items]);

  const addItem = (p) => {
    setItems(list => [{
      product_id: p.id,
      qty: 1,
      extra_allowed: false,
      product: {
        id: p.id, name: p.name, name_tr: p.name_tr, name_en: p.name_en,
        unit: p.unit, extra_price: p.extra_price,
        currency: p.currency, cover_url: p.cover_url, thumb_url: p.thumb_url,
      },
    }, ...list]);
    setCatalogSearch('');
  };

  const removeItem = (idx) => setItems(list => list.filter((_, i) => i !== idx));

  const updateItem = (idx, patch) => setItems(list => list.map((it, i) => i === idx ? { ...it, ...patch } : it));

  const validate = () => {
    const e = {};
    if (!form.modular_stand_id) {
      e.modular_stand_id = tr ? 'Modüler stand seçiniz.' : 'Please select a modular stand.';
    }
    return e;
  };

  const handleSave = async () => {
    const e = validate();
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        area_m2:        Number(form.area_m2),
        name_tr:        form.name_tr.trim(),
        name_en:        form.name_en.trim(),
        description_tr: form.description_tr.trim(),
        description_en: form.description_en.trim(),
        total_price:    Number(form.total_price),
        currency:       form.currency,
        is_active:      form.is_active,
        modular_stand_id: form.modular_stand_id || null,
        items:          items.map(it => ({
          product_id:    it.product_id,
          qty:           Number(it.qty) || 1,
          extra_allowed: !!it.extra_allowed,
        })),
      };
      if (isEdit) {
        await apiClient.put(`/ultra-admin/modular-packages/${pkg.id}`, payload);
      } else {
        await apiClient.post('/ultra-admin/modular-packages', payload);
      }
      toast.success(tr ? 'Paket kaydedildi.' : 'Package saved.');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="package-modal">
      <div className="bg-white rounded-3xl w-full max-w-4xl max-h-[92vh] overflow-hidden shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-blue-50">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-indigo-500 flex items-center justify-center">
              <Layers className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="font-bold text-gray-900 text-lg">
                {isEdit ? (tr ? 'Paketi Düzenle' : 'Edit Package') : (tr ? 'Yeni Paket' : 'New Package')}
              </h2>
              <p className="text-xs text-gray-500">{tr ? 'Seçilen standın paket içeriğini oluşturun.' : 'Build the package contents for the selected stand.'}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/50 text-gray-600" data-testid="close-package-modal">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Modular Stand picker (controls name + area) */}
          <Field
            label={tr ? 'Modüler Stand' : 'Modular Stand'}
            required
            error={errors.modular_stand_id}
          >
            <select
              value={form.modular_stand_id}
              onChange={e => handleStandSelect(e.target.value)}
              className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 bg-white ${errors.modular_stand_id ? 'border-red-300 focus:ring-red-200' : 'border-gray-200 focus:ring-indigo-200'}`}
              data-testid="package-stand-select"
            >
              <option value="">{tr ? '— Stand seçin —' : '— Select a stand —'}</option>
              {standList.map(s => {
                const title = (tr ? (s.title_tr || s.title) : (s.title_en || s.title)) || s.title || s.id;
                // Mark stands already bound to another package as disabled
                const boundPkg = allPackages.find(p => p.modular_stand_id === s.id && (!pkg || p.id !== pkg.id));
                const bound = !!boundPkg;
                const suffix = bound
                  ? (tr ? ' — ✓ Paket oluşturuldu' : ' — ✓ Package created')
                  : '';
                return (
                  <option key={s.id} value={s.id} disabled={bound}>
                    {title}{suffix}
                  </option>
                );
              })}
            </select>
            <p className="text-[11px] text-gray-500 mt-1.5">
              {tr
                ? 'Stand seçtiğinizde paket adı ve m² alanından otomatik eşlenir. Fiyatlandırma stand ürününde yönetilir.'
                : 'Selecting a stand auto-links the package name and m². Pricing is managed on the stand product.'}
            </p>
          </Field>

          {/* Selected stand preview (read-only) */}
          {form.modular_stand_id && (
            <div className="bg-indigo-50 border border-indigo-100 rounded-xl px-4 py-3 flex items-center justify-between">
              <div>
                <p className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider">
                  {tr ? 'Seçilen Stand' : 'Selected Stand'}
                </p>
                <p className="font-bold text-indigo-900 text-sm">{tr ? form.name_tr : form.name_en}</p>
              </div>
              {form.area_m2 && (
                <span className="bg-white border border-indigo-200 text-indigo-700 text-xs font-bold px-2.5 py-1 rounded-full">
                  {form.area_m2} m²
                </span>
              )}
            </div>
          )}

          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input type="checkbox" checked={form.is_active} onChange={e => set('is_active', e.target.checked)} className="h-4 w-4 accent-indigo-500" />
            <span className="text-sm font-medium text-gray-700">{tr ? 'Aktif' : 'Active'}</span>
          </label>

          {/* Items */}
          <div className="border-t border-gray-100 pt-5">
            <h3 className="font-semibold text-gray-900 text-sm mb-3">
              {tr ? `Paket İçeriği (${items.length})` : `Package Items (${items.length})`}
            </h3>

            {/* Selected items */}
            {items.length > 0 && (
              <div className="space-y-2 mb-4">
                {items.map((it, idx) => {
                  const itemName = (tr ? (it.product?.name_tr || it.product?.name) : (it.product?.name_en || it.product?.name)) || '—';
                  return (
                  <div key={idx} className="flex items-center gap-3 bg-gray-50 rounded-xl p-3 border border-gray-100" data-testid={`package-item-row-${idx}`}>
                    {it.product?.thumb_url
                      ? <img src={it.product.thumb_url} alt={itemName} className="h-11 w-11 rounded-lg object-cover flex-shrink-0" />
                      : <div className="h-11 w-11 rounded-lg bg-gray-200 flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{itemName}</p>
                      <p className="text-[10px] text-gray-500">
                        {tr ? 'Birim' : 'Unit'}: {it.product?.unit || 'adet'}
                        {(it.product?.extra_price || 0) > 0 && (
                          <span className="ml-2">· {tr ? 'Ekstra' : 'Extra'}: {it.product.extra_price} {it.product.currency}/{it.product.unit || 'adet'}</span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => updateItem(idx, { qty: Math.max(1, (Number(it.qty) || 1) - 1) })}
                        disabled={Number(it.qty) <= 1}
                        className="h-8 w-8 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-40 flex items-center justify-center"
                        aria-label="decrease"
                        data-testid={`item-qty-minus-${idx}`}
                      >
                        <Minus className="h-3.5 w-3.5 text-gray-700" />
                      </button>
                      <input
                        type="number" min={1}
                        value={it.qty}
                        onChange={e => updateItem(idx, { qty: Math.max(1, Number(e.target.value) || 1) })}
                        className="w-14 border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-center focus:outline-none focus:ring-2 focus:ring-indigo-200"
                        data-testid={`item-qty-${idx}`}
                      />
                      <button
                        type="button"
                        onClick={() => updateItem(idx, { qty: (Number(it.qty) || 1) + 1 })}
                        className="h-8 w-8 rounded-lg bg-indigo-100 hover:bg-indigo-200 text-indigo-700 flex items-center justify-center"
                        aria-label="increase"
                        data-testid={`item-qty-plus-${idx}`}
                      >
                        <Plus className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeItem(idx)}
                        className="ml-1 p-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-600"
                        aria-label="remove"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                );})}
              </div>
            )}

            {/* Product search/add */}
            <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-3">
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-indigo-400" />
                <input
                  type="text"
                  value={catalogSearch}
                  onChange={e => setCatalogSearch(e.target.value)}
                  placeholder={tr ? 'Organizatör ürünlerinden ekle...' : 'Add from organizer products...'}
                  className="w-full pl-10 pr-4 py-2 border border-indigo-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 bg-white"
                  data-testid="package-product-search"
                />
              </div>
              {productCatalog.length === 0 ? (
                <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-2">
                  <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                  <span>
                    {tr
                      ? 'Organizatör ürünü henüz yok. Önce "Organizatör Ürünleri" sayfasından ürün ekleyin.'
                      : 'No organizer products yet. Add some from the "Organizer Products" page first.'}
                  </span>
                </div>
              ) : filteredCatalog.length === 0 ? (
                <p className="text-xs text-gray-500 italic px-1">{tr ? 'Eşleşen ürün yok.' : 'No matching products.'}</p>
              ) : (
                <div className="max-h-48 overflow-y-auto space-y-1" data-testid="package-catalog-list">
                  {filteredCatalog.map(p => {
                    const pName = (tr ? (p.name_tr || p.name) : (p.name_en || p.name)) || p.name;
                    const pCat  = (tr ? (p.category_tr || p.category) : (p.category_en || p.category));
                    const pSub  = (tr ? (p.sub_category_tr || p.sub_category) : (p.sub_category_en || p.sub_category));
                    return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => addItem(p)}
                      className="w-full flex items-center gap-3 text-left bg-white hover:bg-indigo-100 border border-transparent hover:border-indigo-300 rounded-lg p-2 transition-colors"
                    >
                      {p.thumb_url
                        ? <img src={p.thumb_url} alt={pName} className="h-9 w-9 rounded object-cover flex-shrink-0" />
                        : <div className="h-9 w-9 rounded bg-gray-200 flex-shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{pName}</p>
                        <p className="text-[10px] text-gray-500">
                          {pCat || '—'}{pSub ? ` · ${pSub}` : ''}
                          {p.sku && ` · ${p.sku}`}
                        </p>
                      </div>
                      <Plus className="h-4 w-4 text-indigo-500 flex-shrink-0" />
                    </button>
                  );})}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-gray-600 hover:text-gray-800 rounded-xl">
            {tr ? 'Kapat' : 'Close'}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="save-package-btn"
            className="bg-indigo-500 hover:bg-indigo-600 text-white font-semibold px-6 py-2 rounded-xl text-sm disabled:opacity-60 flex items-center gap-2"
          >
            {saving
              ? <><RotateCcw className="h-4 w-4 animate-spin" /> {tr ? 'Kaydediliyor...' : 'Saving...'}</>
              : <><Check className="h-4 w-4" /> {isEdit ? (tr ? 'Güncelle' : 'Update') : (tr ? 'Kaydet' : 'Save')}</>
            }
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, error, children, className = '' }) {
  return (
    <div className={className}>
      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      {children}
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}


/* ─── Bulk-Add-Item Modal ──────────────────────────────────────────────────
 * Bir organizer ürününü birden çok pakete aynı qty + extra_allowed ile
 * topluca ekler. Ürün zaten olan paketlerde atlanır (duplicate koruması).
 * ────────────────────────────────────────────────────────────────────────── */
function BulkAddItemModal({ tr, packages, onClose }) {
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [qty, setQty] = useState(1);
  const [extraAllowed, setExtraAllowed] = useState(true);
  const [target, setTarget] = useState('all'); // 'all' | 'selected'
  const [selectedPkgIds, setSelectedPkgIds] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [summary, setSummary] = useState(null); // { affected_count, skipped_count, skipped }

  // Load active organizer products once
  useEffect(() => {
    apiClient.get('/ultra-admin/organizer-products', { params: { is_active: true } })
      .then(r => setProducts(r.data || []))
      .catch(() => toast.error(tr ? 'Ürünler yüklenemedi.' : 'Failed to load products.'));
    // eslint-disable-next-line
  }, []);

  const filteredProducts = useMemo(() => {
    const q = productSearch.trim().toLowerCase();
    if (!q) return products;
    return products.filter(p => {
      const name = `${p.name_tr || ''} ${p.name_en || ''} ${p.sku || ''}`.toLowerCase();
      return name.includes(q);
    });
  }, [products, productSearch]);

  const selectedProduct = products.find(p => p.id === productId);

  // Sort packages by area_m2 ascending for predictable UI
  const sortedPackages = useMemo(() => {
    return [...(packages || [])].sort((a, b) => (a.area_m2 || 0) - (b.area_m2 || 0));
  }, [packages]);

  const togglePkg = (id) => {
    setSelectedPkgIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAllPkgs = () => setSelectedPkgIds(new Set(sortedPackages.map(p => p.id)));
  const clearAllPkgs  = () => setSelectedPkgIds(new Set());

  const targetCount = target === 'all' ? sortedPackages.length : selectedPkgIds.size;
  const canSave = !!productId && targetCount > 0 && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const body = {
        product_id:    productId,
        qty:           Math.max(1, Number(qty) || 1),
        extra_allowed: !!extraAllowed,
        target,
        package_ids:   target === 'selected' ? Array.from(selectedPkgIds) : undefined,
      };
      const r = await apiClient.post('/ultra-admin/modular-packages/bulk-add-item', body);
      setSummary(r.data || null);
      const affected = r.data?.affected_count ?? 0;
      const skipped  = r.data?.skipped_count ?? 0;
      if (affected > 0) {
        toast.success(tr
          ? `${affected} pakete eklendi${skipped ? `, ${skipped} pakette zaten vardı` : ''}.`
          : `Added to ${affected} package(s)${skipped ? `, skipped ${skipped} (already present)` : ''}.`);
      } else if (skipped > 0) {
        toast.info(tr
          ? `Ürün tüm hedef paketlerde zaten vardı (${skipped} paket atlandı).`
          : `Product already in all target packages (${skipped} skipped).`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || (tr ? 'Ekleme başarısız.' : 'Bulk add failed.'));
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    const didChange = !!(summary?.affected_count);
    onClose(didChange);
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="bulk-add-item-modal">
      <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[92vh] overflow-hidden shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-amber-50 to-orange-50">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-amber-500 flex items-center justify-center">
              <CopyPlus className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="font-bold text-gray-900 text-lg">
                {tr ? 'Toplu Ürün Ekle' : 'Bulk Add Product'}
              </h2>
              <p className="text-xs text-gray-500">
                {tr
                  ? 'Bir ürünü birden çok modüler pakete aynı anda ekle. Ürün zaten olan paketler atlanır.'
                  : 'Add one product to multiple modular packages at once. Packages that already contain the product are skipped.'}
              </p>
            </div>
          </div>
          <button onClick={handleClose} className="p-1.5 rounded-lg hover:bg-white/50 text-gray-600" data-testid="close-bulk-add-modal">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Success summary */}
          {summary && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4" data-testid="bulk-add-summary">
              <div className="flex items-start gap-2.5 mb-2">
                <Check className="h-5 w-5 text-emerald-700 mt-0.5 flex-shrink-0" />
                <p className="text-sm font-semibold text-emerald-900">
                  {tr
                    ? `${summary.affected_count} pakete eklendi, ${summary.skipped_count} atlandı (${summary.scanned_count} paket tarandı).`
                    : `Added to ${summary.affected_count} package(s), ${summary.skipped_count} skipped (${summary.scanned_count} scanned).`}
                </p>
              </div>
              {summary.skipped_count > 0 && (
                <div className="pl-7 space-y-1">
                  <p className="text-[11px] font-bold text-amber-800 uppercase tracking-wider">
                    {tr ? 'Atlanan paketler (ürün zaten mevcut):' : 'Skipped packages (product already in):'}
                  </p>
                  <ul className="text-xs text-gray-700 list-disc pl-4">
                    {(summary.skipped || []).map(s => (
                      <li key={s.package_id}>{s.package_name}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Product picker */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              {tr ? 'Ürün' : 'Product'} <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={productSearch}
                onChange={e => setProductSearch(e.target.value)}
                placeholder={tr ? 'Ürün adı veya SKU ara...' : 'Search product name or SKU...'}
                data-testid="bulk-product-search"
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-200"
              />
            </div>
            <div className="mt-2 border border-gray-200 rounded-xl max-h-56 overflow-y-auto divide-y divide-gray-100">
              {filteredProducts.length === 0 ? (
                <div className="p-4 text-center text-sm text-gray-400">
                  {tr ? 'Eşleşen aktif ürün yok.' : 'No matching active products.'}
                </div>
              ) : (
                filteredProducts.map(p => {
                  const name = (tr ? (p.name_tr || p.name_en) : (p.name_en || p.name_tr)) || p.name;
                  const sub  = (tr ? (p.category_tr || p.category) : (p.category_en || p.category)) || '';
                  const isSelected = productId === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setProductId(p.id)}
                      data-testid={`bulk-product-option-${p.id}`}
                      className={`w-full text-left px-3 py-2 flex items-center gap-3 transition-colors ${isSelected ? 'bg-amber-50' : 'hover:bg-gray-50'}`}
                    >
                      <div className={`h-4 w-4 rounded-full border-2 flex-shrink-0 ${isSelected ? 'border-amber-500 bg-amber-500' : 'border-gray-300'}`}>
                        {isSelected && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{name}</p>
                        <p className="text-[11px] text-gray-500">{sub} {p.sku ? `· ${p.sku}` : ''}</p>
                      </div>
                      <span className="text-xs font-semibold text-gray-600 flex-shrink-0">
                        {Number(p.extra_price || 0).toFixed(2)} {p.currency || 'EUR'}/{p.unit || 'adet'}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
            {selectedProduct && (
              <p className="text-[11px] text-emerald-700 mt-1.5 font-semibold">
                ✓ {tr ? 'Seçildi' : 'Selected'}: {(tr ? selectedProduct.name_tr : selectedProduct.name_en) || selectedProduct.name}
              </p>
            )}
          </div>

          {/* Qty + extra_allowed */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                {tr ? 'Adet' : 'Quantity'}
              </label>
              <input
                type="number"
                min={1}
                step={1}
                value={qty}
                onChange={e => setQty(e.target.value)}
                data-testid="bulk-qty-input"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-200"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                {tr ? 'Ekstra İzin' : 'Extra Allowed'}
              </label>
              <label className="flex items-center gap-2 h-[42px] px-4 border border-gray-200 rounded-xl cursor-pointer hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={extraAllowed}
                  onChange={e => setExtraAllowed(e.target.checked)}
                  data-testid="bulk-extra-allowed-toggle"
                  className="h-4 w-4 accent-amber-500"
                />
                <span className="text-sm text-gray-700">
                  {tr ? 'Müşteri ekstra ekleyebilir' : 'Customer may add extras'}
                </span>
              </label>
            </div>
          </div>

          {/* Target selector */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {tr ? 'Hedef Paketler' : 'Target Packages'} <span className="text-red-400">*</span>
            </label>
            <div className="space-y-2">
              <label className="flex items-start gap-2.5 p-3 border-2 border-gray-200 rounded-xl cursor-pointer hover:bg-gray-50 has-[:checked]:border-amber-400 has-[:checked]:bg-amber-50">
                <input
                  type="radio"
                  name="bulk-target"
                  value="all"
                  checked={target === 'all'}
                  onChange={() => setTarget('all')}
                  data-testid="bulk-target-all"
                  className="mt-0.5 accent-amber-500"
                />
                <div>
                  <p className="text-sm font-semibold text-gray-900">
                    {tr ? 'Tüm paketlere ekle' : 'Add to all packages'}
                  </p>
                  <p className="text-[11px] text-gray-500">
                    {tr
                      ? `Şu an ${sortedPackages.length} paket bulunuyor (aktif + pasif).`
                      : `${sortedPackages.length} package(s) currently exist (active + inactive).`}
                  </p>
                </div>
              </label>
              <label className="flex items-start gap-2.5 p-3 border-2 border-gray-200 rounded-xl cursor-pointer hover:bg-gray-50 has-[:checked]:border-amber-400 has-[:checked]:bg-amber-50">
                <input
                  type="radio"
                  name="bulk-target"
                  value="selected"
                  checked={target === 'selected'}
                  onChange={() => setTarget('selected')}
                  data-testid="bulk-target-selected"
                  className="mt-0.5 accent-amber-500"
                />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-900">
                    {tr ? 'Seçili paketlere ekle' : 'Add to selected packages'}
                  </p>
                  <p className="text-[11px] text-gray-500">
                    {tr ? 'Aşağıdan bir veya birden fazla paket seç.' : 'Tick one or more packages below.'}
                  </p>
                </div>
              </label>
            </div>

            {target === 'selected' && (
              <div className="mt-3 border border-gray-200 rounded-xl overflow-hidden">
                <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200">
                  <span className="text-xs font-semibold text-gray-600">
                    {tr ? `${selectedPkgIds.size} / ${sortedPackages.length} seçili` : `${selectedPkgIds.size} / ${sortedPackages.length} selected`}
                  </span>
                  <div className="flex gap-2">
                    <button type="button" onClick={selectAllPkgs} className="text-xs font-semibold text-amber-700 hover:text-amber-800">
                      {tr ? 'Hepsini Seç' : 'Select All'}
                    </button>
                    <span className="text-gray-300">·</span>
                    <button type="button" onClick={clearAllPkgs} className="text-xs font-semibold text-gray-600 hover:text-gray-800">
                      {tr ? 'Temizle' : 'Clear'}
                    </button>
                  </div>
                </div>
                <div className="max-h-48 overflow-y-auto divide-y divide-gray-100">
                  {sortedPackages.length === 0 ? (
                    <div className="p-4 text-center text-sm text-gray-400">
                      {tr ? 'Paket yok.' : 'No packages.'}
                    </div>
                  ) : sortedPackages.map(pkg => {
                    const pkgName = (tr ? (pkg.name_tr || pkg.name_en) : (pkg.name_en || pkg.name_tr)) || pkg.name || '—';
                    const isChecked = selectedPkgIds.has(pkg.id);
                    return (
                      <label
                        key={pkg.id}
                        className={`flex items-center gap-3 px-3 py-2 cursor-pointer ${isChecked ? 'bg-amber-50' : 'hover:bg-gray-50'}`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => togglePkg(pkg.id)}
                          data-testid={`bulk-pkg-checkbox-${pkg.id}`}
                          className="h-4 w-4 accent-amber-500"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">{pkgName}</p>
                          <p className="text-[11px] text-gray-500">
                            {pkg.area_m2 ? `${pkg.area_m2} m²` : '—'}
                            {pkg.is_active === false ? (tr ? ' · Pasif' : ' · Inactive') : ''}
                          </p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Preview */}
          {productId && targetCount > 0 && !summary && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-amber-700 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-900">
                {tr
                  ? <>Onaylarsan <strong>{targetCount}</strong> pakete <strong>{qty}</strong> adet eklenir. Ürün zaten olan paketler atlanır.</>
                  : <>On confirm, <strong>{qty}</strong> unit(s) will be added to <strong>{targetCount}</strong> package(s). Packages already containing the product are skipped.</>}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-semibold text-gray-600 hover:text-gray-800 rounded-xl transition-colors"
            data-testid="bulk-cancel-btn"
          >
            {summary ? (tr ? 'Kapat' : 'Close') : (tr ? 'Vazgeç' : 'Cancel')}
          </button>
          {!summary && (
            <button
              onClick={handleSave}
              disabled={!canSave}
              data-testid="bulk-save-btn"
              className="bg-amber-500 hover:bg-amber-600 text-white font-semibold px-6 py-2 rounded-xl text-sm transition-colors disabled:opacity-60 flex items-center gap-2"
            >
              {saving
                ? <><RotateCcw className="h-4 w-4 animate-spin" /> {tr ? 'Ekleniyor...' : 'Adding...'}</>
                : <><CopyPlus className="h-4 w-4" /> {tr ? `${targetCount} pakete ekle` : `Add to ${targetCount} package(s)`}</>
              }
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
